/**
 * PopupReportPage - Stateless report viewer popup
 *
 * This popup receives all data from the main window via BroadcastChannel.
 * It does NOT create its own WebSocket connections.
 *
 * Flow:
 * 1. Popup opens and reads popupId from URL
 * 2. Popup sends POPUP_INIT request to main window
 * 3. Main window responds with POPUP_INIT_RESPONSE containing report data
 * 4. Popup renders the data
 * 5. User actions (zoom to ship, export) are sent back to main window
 */
import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import {
  Button,
  LoadingSpinner,
  ScrollArea,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@ais-ui/ui-core';
import {
  Job,
  ParsedReportParams,
  ReportShipData,
  ReportTemplate,
} from '@ais-ui/websockets';
import { X } from 'lucide-react';
import {
  getPopupIdFromUrl,
  getPopupParams,
  closePopup,
  PopupType,
} from '@ais-ui/popups';
import {
  PopupActions,
  MessageType,
  useMainWindowMessages,
  PopupMessage,
  DataType,
} from './PopupEvents';
import { usePopupLifecycle } from './usePopupLifecycle';
import { PopupConnectionStatus } from './shared/PopupConnectionStatus';
import { usePopupPerformanceTracking } from './usePopupPerformanceTracking';
import { PopupReportTable } from './PopupReportTable';
import { PopupReportChart, reportTypeHasTableData } from './PopupReportChart';
import { ReportDetailsContent } from '../Reports/ReportDetailsContent';
import { getReportTypeLabel } from '../Reports/reportsTypes';
import { useDateTimeFormat } from '../conversions/useDateFormat';

/**
 * Data structure sent from main window
 */
export interface PopupReportData {
  job: Job;
  reportTemplate: ReportTemplate;
  report: ParsedReportParams | null;
  shipData: ReportShipData[];
  exportTypes?: string[];
  error?: string;
}

// Status enum for clear state management
type PopupStatus = 'idle' | 'requesting' | 'ready' | 'error' | 'timeout';

export const PopupReportPage: React.FC = () => {
  const popupId = getPopupIdFromUrl();
  const params = getPopupParams();
  const jobId = params.jobId;
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const [data, setData] = useState<PopupReportData | null>(null);
  const [status, setStatus] = useState<PopupStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Performance tracking - stable metadata reference
  const trackingMetadata = useMemo(
    () => ({ jobId: jobId || 'unknown' }),
    [jobId],
  );
  const { trackDataRequest, trackDataReceived, trackError, trackTimeout } =
    usePopupPerformanceTracking({
      popupId,
      popupType: PopupType.Report,
      metadata: trackingMetadata,
    });

  // Stable callback refs for tracking (avoids dependency issues)
  const trackingRef = useRef({
    trackDataRequest,
    trackDataReceived,
    trackError,
    trackTimeout,
  });
  useEffect(() => {
    trackingRef.current = {
      trackDataRequest,
      trackDataReceived,
      trackError,
      trackTimeout,
    };
  }, [trackDataRequest, trackDataReceived, trackError, trackTimeout]);

  // Request initial data - runs once when popupId/jobId are available
  useEffect(() => {
    if (!popupId) {
      setStatus('error');
      setErrorMessage('No popup ID found');
      trackingRef.current.trackError('No popup ID found');
      return;
    }

    // Only request if idle (prevents re-requests on re-renders)
    if (status !== 'idle') return;

    setStatus('requesting');
    trackingRef.current.trackDataRequest();

    PopupActions.requestInit({
      popupId,
      popupType: PopupType.Report,
      params: { jobId: jobId || '', requestShipData: true },
    });

    // Timeout handler - 30 seconds to allow for bundle loading + API fetches
    const timeout = setTimeout(() => {
      setStatus((prev) => {
        if (prev === 'requesting') {
          setErrorMessage(
            'Timeout waiting for data from main window. Please close and reopen.',
          );
          trackingRef.current.trackTimeout();
          return 'timeout';
        }
        return prev;
      });
    }, 30000);

    return () => clearTimeout(timeout);
  }, [popupId, jobId, status]);

  // Listen for messages from main window - stable callback
  const handleMessage = useCallback(
    (msg: PopupMessage) => {
      if (
        msg.type === MessageType.POPUP_INIT_RESPONSE &&
        msg.popupId === popupId
      ) {
        const payload = msg.payload as PopupReportData;
        setStatus((prev) => {
          // Only process if we're still waiting for data
          if (prev !== 'requesting') return prev;

          setData(payload);
          setErrorMessage(null);
          trackingRef.current.trackDataReceived(payload.shipData?.length);

          if (payload.job?.name) {
            document.title = `Report: ${payload.job.name}`;
          }
          return 'ready';
        });
      }

      if (msg.type === MessageType.DATA_UPDATE && msg.popupId === popupId) {
        const payload = msg.payload as {
          dataType: DataType;
          data: { data: unknown; error?: string };
        };
        if (payload.dataType === DataType.SHIP_DATA) {
          const shipDataPayload = payload.data;
          setData((prev) =>
            prev
              ? { ...prev, shipData: shipDataPayload.data as ReportShipData[] }
              : null,
          );
        }
      }

      if (msg.type === MessageType.SESSION_ENDED) {
        setErrorMessage('Session ended. Please close this window.');
        setStatus('error');
        trackingRef.current.trackError('Session ended');
      }
    },
    [popupId],
  );

  useMainWindowMessages(handleMessage, popupId ?? undefined, [handleMessage]);

  // Handle popup lifecycle (close notification, heartbeat)
  const connectionState = usePopupLifecycle(popupId, PopupType.Report);

  // Derived state
  const isLoading = status === 'idle' || status === 'requesting';
  const hasError = status === 'error' || status === 'timeout';

  // Date formatter using user settings
  const formatDateTime = (date: Date | null | undefined): string => {
    if (!date) return '-';
    return formatDateTimeWithTimeZone(date);
  };

  if (hasError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-8 bg-background">
        <div className="text-destructive text-lg font-medium mb-4">Error</div>
        <div className="text-muted-foreground text-center mb-4">
          {errorMessage}
        </div>
        <Button onClick={closePopup}>Close Window</Button>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <LoadingSpinner />
        <p className="mt-4 text-muted-foreground">Loading report data...</p>
      </div>
    );
  }

  const { job, reportTemplate, report, shipData, exportTypes } = data;

  // Handle case where job wasn't found/selected in main window
  if (!job || !reportTemplate) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-8 bg-background">
        <div className="text-destructive text-lg font-medium mb-4">
          Report Not Available
        </div>
        <div className="text-muted-foreground text-center mb-4">
          {data.error ||
            'The report could not be found. It may have been deleted.'}
        </div>
        <Button onClick={closePopup}>Close Window</Button>
      </div>
    );
  }

  const reportName = job.name || 'Report Details';
  const reportType = reportTemplate.type;
  const reportTypeLabel = getReportTypeLabel(reportType);
  const hasTableData = reportTypeHasTableData(reportType);

  return (
    <div className="h-screen flex flex-col bg-gradient-to-b from-primary/5 via-background to-background">
      {/* Connection status overlay */}
      <PopupConnectionStatus connectionState={connectionState} />

      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{reportName}</h1>
            <p className="text-muted-foreground">{reportTypeLabel} Report</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={closePopup}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Top section with tabs for Chart/Details */}
        <div
          className={`${hasTableData ? 'flex-1' : 'flex-[2]'} min-h-0 overflow-hidden ${hasTableData ? 'border-b' : ''}`}
        >
          <Tabs defaultValue="chart" className="h-full flex flex-col">
            <div className="flex-shrink-0 px-6 pt-4">
              <TabsList>
                <TabsTrigger value="chart">Charts</TabsTrigger>
                <TabsTrigger value="details">Details</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent
              value="chart"
              className="flex-1 overflow-hidden m-0 px-6 pb-4"
            >
              <div className="h-full pt-4">
                <div className="h-full border rounded-lg p-4">
                  <PopupReportChart
                    shipData={shipData}
                    isLoading={false}
                    reportType={reportType}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="details" className="flex-1 overflow-hidden m-0">
              <ScrollArea className="h-full">
                <div className="p-6">
                  <ReportDetailsContent
                    report={report as ParsedReportParams | undefined}
                    reportTemplate={reportTemplate}
                    job={job}
                    formatDateTimeWithTimeZone={formatDateTime}
                    variant="popup"
                  />
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>

        {/* Bottom section - Table (only for report types with table data) */}
        {hasTableData && (
          <div className="flex-1 min-h-0 overflow-hidden px-6 py-4">
            <PopupReportTable
              shipData={shipData}
              isLoading={false}
              jobId={jobId || undefined}
              reportType={reportType}
            />
          </div>
        )}
      </div>
    </div>
  );
};
